/**
 * LocationSearch — Unified Hybrid Location Autocomplete
 * =====================================================
 * Google Places (primary for long queries) + MongoDB (primary for short queries).
 * Falls back to the other source on failure.
 *
 * Cost-optimised:
 *   - query.length < 4  → DB first, Google fallback
 *   - query.length >= 4  → Google first, DB fallback
 *   - Frontend Map cache (5-min TTL)
 *   - Session tokens, debounce 300ms, min 3 chars, India-only
 *   - Field restriction on getDetails
 *
 * Props (backward-compatible with IndiaLocationAutocomplete):
 *   value, onChange, onLocationSelect, placeholder, types, state,
 *   className, inputClassName, required, disabled, data-testid, limit
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2, X, Building2, Home, MapPinned, Navigation } from 'lucide-react';
import { Input } from '@/components/ui/input';
import apiClient from '@/utils/api';
import { getConfig } from '@/utils/runtimeConfig';

// ---------- constants ----------
const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 3;
const GOOGLE_THRESHOLD = 4;     // >= this length → Google first
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ---------- frontend query cache ----------
const _cache = new Map();

function cacheKey(q, source) {
  return `${source}:${q.toLowerCase().trim()}`;
}

function cacheGet(key) {
  const entry = _cache.get(key);
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  _cache.delete(key);
  return null;
}

function cacheSet(key, data) {
  _cache.set(key, { data, ts: Date.now() });
  if (_cache.size > 500) {
    const oldest = _cache.keys().next().value;
    _cache.delete(oldest);
  }
}

// ---------- Google Places (NEW API) script loader ----------
// Uses v=weekly (required for importLibrary) so we can fetch `AutocompleteSuggestion`
// (the modern replacement for `AutocompleteService.getPlacePredictions`).
let _scriptPromise = null;

function loadGoogleScript() {
  if (_scriptPromise) return _scriptPromise;
  if (window.google?.maps?.importLibrary) {
    _scriptPromise = Promise.resolve();
    return _scriptPromise;
  }

  const { google_places_key } = getConfig();
  if (!google_places_key) return Promise.reject(new Error('No Google Places key'));

  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-places-script');
    if (existing) { existing.addEventListener('load', resolve); return; }

    const s = document.createElement('script');
    s.id = 'google-places-script';
    // v=weekly is required for the new Places API (AutocompleteSuggestion / Place.fetchFields).
    s.src = `https://maps.googleapis.com/maps/api/js?key=${google_places_key}&v=weekly&loading=async&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// ---------- type helpers ----------
const TYPE_ICONS = {
  city: Building2, town: Building2, locality: MapPinned,
  village: Home, district: Navigation, state: Navigation, pincode: MapPin,
};
const TYPE_LABELS = {
  city: 'City', town: 'Town', locality: 'Locality',
  village: 'Village', district: 'District', state: 'State', pincode: 'Pincode',
};

function mapTypesToGoogle(types) {
  // New Places API supports the `(regions)` type collection, which includes
  // localities, sublocalities, villages, postal codes and administrative areas.
  // Using it ensures smaller towns/villages surface in autocomplete — not just
  // major cities (the previous `locality`-only restriction hid villages).
  if (!types) return ['(regions)'];
  const arr = Array.isArray(types) ? types : types.split(',').map((t) => t.trim());
  if (arr.includes('state')) return ['administrative_area_level_1'];
  // city / town / village / locality / pincode → broad regions collection
  return ['(regions)'];
}

// ---------- Component ----------
const LocationSearch = ({
  value,
  onChange,
  placeholder = 'Search city, locality, pincode...',
  types = null,
  state = null,
  className = '',
  inputClassName = '',
  required = false,
  disabled = false,
  'data-testid': testId = 'location-search',
  onLocationSelect,
  limit = 10,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [googleReady, setGoogleReady] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const placesLib = useRef(null);          // { AutocompleteSuggestion, AutocompleteSessionToken }
  const sessionToken = useRef(null);

  // ---- sync external value ----
  useEffect(() => { setInputValue(value || ''); }, [value]);

  // ---- click outside ----
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ---- init Google (new Places API) ----
  useEffect(() => {
    let cancelled = false;
    loadGoogleScript()
      .then(async () => {
        const lib = await window.google.maps.importLibrary('places');
        if (cancelled) return;
        placesLib.current = lib;
        sessionToken.current = new lib.AutocompleteSessionToken();
        setGoogleReady(true);
      })
      .catch(() => { /* Google unavailable — DB-only mode */ });
    return () => { cancelled = true; };
  }, []);

  // ---- DB fetch ----
  const fetchDB = useCallback(async (query) => {
    const key = cacheKey(query, 'db');
    const cached = cacheGet(key);
    if (cached) return cached;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    const params = new URLSearchParams({ q: query, limit });
    if (types) params.append('types', Array.isArray(types) ? types.join(',') : types);
    if (state) params.append('state', state);

    const { data } = await apiClient.get(`/locations/autocomplete?${params}`, {
      signal: abortRef.current.signal,
    });

    const results = (data || []).map((loc) => ({ ...loc, _source: 'db' }));
    cacheSet(key, results);
    return results;
  }, [types, state, limit]);

  // ---- Google fetch (new Places API: AutocompleteSuggestion.fetchAutocompleteSuggestions) ----
  const fetchGoogle = useCallback(async (query) => {
    if (!placesLib.current) return [];

    const key = cacheKey(query, 'google');
    const cached = cacheGet(key);
    if (cached) return cached;

    const primaryTypes = mapTypesToGoogle(types);
    const request = {
      input: query,
      sessionToken: sessionToken.current,
      includedRegionCodes: ['in'],
      ...(primaryTypes ? { includedPrimaryTypes: primaryTypes } : {}),
    };

    try {
      const { suggestions } = await placesLib.current.AutocompleteSuggestion
        .fetchAutocompleteSuggestions(request);
      const mapped = (suggestions || [])
        .filter((s) => s.placePrediction)
        .map((s) => {
          const p = s.placePrediction;
          const mainText = p.mainText?.text || p.text?.text || '';
          const secondaryText = p.secondaryText?.text || '';
          const description = p.text?.text || [mainText, secondaryText].filter(Boolean).join(', ');
          return {
            name: mainText || description.split(',')[0],
            display_name: description,
            _source: 'google',
            _placeId: p.placeId,
            _placePrediction: p,      // kept so getPlaceDetails can call toPlace()
            _mainText: mainText,
            _secondaryText: secondaryText,
          };
        });
      cacheSet(key, mapped);
      return mapped;
    } catch {
      return [];
    }
  }, [types]);

  // ---- hybrid search ----
  const search = useCallback(async (query) => {
    if (!query || query.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);

    try {
      let results = [];

      if (query.length < GOOGLE_THRESHOLD || !googleReady) {
        // DB first
        try {
          results = await fetchDB(query);
        } catch (e) {
          if (e?.code === 'ERR_CANCELED') return;
        }
        if ((!results || results.length === 0) && googleReady) {
          results = await fetchGoogle(query);
        }
      } else {
        // Google first
        results = await fetchGoogle(query);
        if (!results || results.length === 0) {
          try {
            results = await fetchDB(query);
          } catch (e) {
            if (e?.code === 'ERR_CANCELED') return;
          }
        }
      }

      setSuggestions(results || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [googleReady, fetchDB, fetchGoogle]);

  // ---- input change (debounced) ----
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setSelectedIndex(-1);
    onChange?.(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), DEBOUNCE_MS);
  };

  // ---- get place details (new Places API: Place.fetchFields) ----
  const getPlaceDetails = useCallback(async (placePrediction) => {
    if (!placePrediction) return null;
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['id', 'location', 'addressComponents', 'displayName', 'formattedAddress'],
      });
      const c = {};
      (place.addressComponents || []).forEach((comp) => {
        const types = comp.types || [];
        if (types.includes('locality')) c.city = comp.longText;
        if (types.includes('administrative_area_level_1')) c.state = comp.longText;
        if (types.includes('administrative_area_level_2')) c.district = comp.longText;
        if (types.includes('postal_code')) c.pincode = comp.longText;
        if (types.includes('sublocality_level_1')) c.locality = comp.longText;
        if (types.includes('country')) c.country = comp.longText;
      });
      const loc = place.location;
      const result = {
        place_id: place.id,
        name: place.displayName || '',
        lat: typeof loc?.lat === 'function' ? loc.lat() : loc?.lat,
        lng: typeof loc?.lng === 'function' ? loc.lng() : loc?.lng,
        formattedAddress: place.formattedAddress,
        ...c,
      };
      // Renew session token after detail fetch (billing boundary).
      if (placesLib.current) {
        sessionToken.current = new placesLib.current.AutocompleteSessionToken();
      }
      return result;
    } catch {
      return null;
    }
  }, []);

  // ---- selection handler ----
  const handleSelect = useCallback(async (item) => {
    const displayVal = item.name;
    setInputValue(displayVal);
    setShowSuggestions(false);
    setSuggestions([]);
    onChange?.(displayVal);

    if (item._source === 'google' && item._placePrediction) {
      const details = await getPlaceDetails(item._placePrediction);
      if (details) {
        onLocationSelect?.({
          name: details.name || displayVal,
          type: details.city ? 'city' : 'locality',
          city: details.city || details.name,
          district: details.district,
          state: details.state,
          pincode: details.pincode,
          lat: details.lat,
          lng: details.lng,
          locality: details.locality,
          place_id: details.place_id,
          country: details.country || 'India',
          display_name: item.display_name,
          _source: 'google',
        });
      }
    } else {
      // DB result — already has full data
      onLocationSelect?.({
        name: item.name,
        type: item.type,
        city: item.city || item.name,
        district: item.district,
        state: item.state,
        pincode: item.pincode,
        lat: item.lat,
        lng: item.lng,
        display_name: item.display_name,
        _source: 'db',
      });
    }
  }, [onChange, onLocationSelect, getPlaceDetails]);

  // ---- keyboard nav ----
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((p) => (p < suggestions.length - 1 ? p + 1 : p));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((p) => (p > 0 ? p - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) handleSelect(suggestions[selectedIndex]);
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (inputValue.length >= MIN_QUERY_LEN && suggestions.length > 0) setShowSuggestions(true);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange?.('');
    onLocationSelect?.(null);
    inputRef.current?.focus();
  };

  // ---- render ----
  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`pl-10 pr-10 ${inputClassName} ${disabled ? 'bg-gray-100' : ''}`}
          data-testid={testId}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {!loading && inputValue && (
          <button type="button" onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testId}-suggestions`}>
          {suggestions.map((item, idx) => {
            const isGoogle = item._source === 'google';
            const TypeIcon = isGoogle ? MapPin : (TYPE_ICONS[item.type] || MapPin);

            return (
              <button key={`${item.name}-${item._placeId || item.state}-${idx}`}
                type="button"
                onClick={() => handleSelect(item)}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-start gap-3 transition-colors border-b border-gray-100 last:border-b-0 ${
                  idx === selectedIndex ? 'bg-primary/10' : ''
                }`}
                data-testid={`${testId}-suggestion-${idx}`}>
                <TypeIcon className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{item.name}</p>
                    {!isGoogle && item.type && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded uppercase font-medium">
                        {TYPE_LABELS[item.type] || item.type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {isGoogle
                      ? item._secondaryText || item.display_name
                      : item.display_name || `${item.state || ''}${item.pincode ? ` \u00B7 ${item.pincode}` : ''}`}
                  </p>
                </div>
              </button>
            );
          })}
          <div className="px-3 py-2 text-[10px] text-gray-400 bg-gray-50 flex items-center justify-between">
            <span>{suggestions.length} results</span>
            {suggestions.some((s) => s._source === 'google') ? (
              <span className="flex items-center gap-1">
                Powered by
                <img src="https://developers.google.com/static/maps/documentation/images/google_on_white.png"
                  alt="Google" className="h-3" />
              </span>
            ) : (
              <span>ZenStudios Location Service</span>
            )}
          </div>
        </div>
      )}

      {showSuggestions && inputValue.length >= MIN_QUERY_LEN && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          data-testid={`${testId}-no-results`}>
          <p className="text-sm text-gray-500 text-center">
            No matching locations found. Try a different search.
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
